import React, { useContext } from "react";
import moment from "moment";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import ThemeContext from "../../../utils/Contexts/ThemeContext";
import VisaTransaction from "../../svg/VisaTransaction";
import PaypalTransaction from "../../svg/paypalTransaction";
import DiscoverTransaction from "../../svg/DiscoverTransaction";
import EftposTransaction from "../../svg/EftposAustraliaTransaction";
import AmericanTransaction from "../../svg/AmericanTransaction";
import GirocardTransaction from "../../svg/GirocardTransaction";
import JcbTransaction from "../../svg/JcbTransaction";
import MatercardTransaction from "../../svg/MastercardTransaction";
import DinersTransaction from "../../svg/DinersTransaction";
import UnionTransaction from "../../svg/UnionpayTransaction";
import GoogleTransaction from "../../svg/GoogleTransaction";
import AppleTransaction from "../../svg/AppleTransaction";

function Transaction({ transactions }) {
  const { t } = useContext(LanguageContext);
  const { selectedTheme } = useContext(ThemeContext);

  const transactionTypes = Object.fromEntries(
    Object.entries({
      1: "Charge",
      2: "Non-Withdrawable Credits Deposit",
      3: "Non-Withdrawable Credits Purchase Bonus",
      4: "Credits Purchase Bonus",
      5: "Deposit",
      6: "Contract Payment",
      7: "Income Conversion to Credits",
      8: "Order Payment (Non-Withdrawable Credits)",
      9: "Order Payment (Credits)",
      10: "Contract Warranty Hold",
      11: "Contract Warranty Hold Released",
      12: "Withdrawal",
      13: "Contract Warranty Hold Canceled",
      14: "Contract Payment Refund (Non-Withdrawable Credits)",
      15: "Contract Payment Refund (Credits)",
      16: "Contract Payment Refund (Credit Card)",
      17: "Contract Payment Refund (PayPal)",
      100: "Withdrawal",
      200: "Order Payment",
      201: "Order Payment Refund",
      202: "Contract Payment Refund",
      203: "Credits Purchase",
      204: "Credits Deposit",
      300: "Incoming Contract Payment",
      900: "Payment Processing Fee",
    }).map(([key, value]) => [key, t(value)])
  );

  const cardBrandTypes = {
    0: "Unknown",
    1: <AmericanTransaction />,
    2: <DinersTransaction />,
    3: <DiscoverTransaction />,
    4: <EftposTransaction />,
    5: <GirocardTransaction />,
    6: <JcbTransaction />,
    7: <MatercardTransaction />,
    8: <UnionTransaction />,
    9: <VisaTransaction />,
  };

  const transactionStatusTypes = {
    100: { label: t("Pending"), className: "bg-soft-primary text-primary" },
    101: { label: t("Processing"), className: "bg-soft-primary text-primary" },
    102: { label: t("Canceled"), className: "bg-soft-danger text-danger" },
    103: { label: t("Failed"), className: "bg-soft-danger text-danger" },
    200: { label: t("Completed"), className: "bg-soft-success text-success" },
    300: {
      label: t("Warranty Hold"),
      className: "bg-softer-warning text-warning",
    },
  };

  const localizeDate = (date) => {
    const localizeDateText = moment(date).format("MMM DD, YYYY, h:mm");
    const mappings = {
      Jan: t("Jan"),
      Feb: t("Feb"),
      Mar: t("Mar"),
      Apr: t("Apr"),
      May: t("May"),
      Jun: t("Jun"),
      Jul: t("Jul"),
      Aug: t("Aug"),
      Sep: t("Sep"),
      Oct: t("Oct"),
      Nov: t("Nov"),
      Dec: t("Dec"),
    };
    return localizeDateText
      .split(" ")
      .map((word) => mappings[word] || word)
      .join(" ");
  };

  return (
    <div id="guestsSection" className="card mb-4">
      <div className="card-header d-flex justify-content-between ps-3 pb-3 pt-4">
        <h4 className="card-title">{t("Transactions Timeline")}</h4>
      </div>
      <div className="table-responsive datatable-custom">
        <table
          className="table table-thead-bordered table-nowrap card-table"
          style={{ border: "transparent" }}
        >
          <thead className="thead-light">
            <tr>
              <th>{t("TRANSACTION")}</th>
              <th className="ps-5 pe-0">{t("PAYMENT METHOD")}</th>
              <th className="px-0">{t("STATUS")}</th>
              <th className="px-0">{t("AMOUNT")}</th>
              <th>{t("DATE")}</th>
            </tr>
          </thead>

          <tbody>
            {!!transactions?.length ? (
              transactions?.map((item, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td className="pt-3">
                      <div className="d-flex flex-column gap-2">
                        <div>
                          <span className="h4 text-inherit font-table">
                            {transactionTypes[item?.type]}
                          </span>
                        </div>

                        <div
                          style={{
                            color: "rgba(145, 146, 147, 1)",
                            fontSize: "14px",
                          }}
                        >
                          {item?.code}
                        </div>
                      </div>
                    </td>

                    <td className="align-middle pt-3 ps-5 pe-3 pe-lg-1">
                      <div
                        className={`d-flex align-items-center bg-soft-secondary gap-2 transaction-payment-badge ${
                          item?.method !== 1 ? "ps-2" : "justify-content-center"
                        }`}
                      >
                        <div>
                          {item?.method === 2 &&
                            cardBrandTypes[item?.method_brand]}
                          {item?.method === 3 && <PaypalTransaction />}
                          {item?.method === 4 && <GoogleTransaction />}
                          {item?.method === 5 && <AppleTransaction />}
                          {item?.method === 1 && (
                            <span
                              style={{ alignContent: "center" }}
                              className={`badge text-secondary transaction-payment-badge`}
                            >
                              {t("Internal Transfer")}
                            </span>
                          )}
                        </div>

                        {item?.method !== 1 && (
                          <div className="text-secondary">
                            &bull;&bull;&bull;&bull; {item?.method_identifier}
                          </div>
                        )}
                      </div>
                    </td>

                    <td className="align-middle pt-3 ps-0 pe-3 pe-lg-1">
                      <span
                        className={`d-flex align-items-center justify-content-center badge transaction-badge ${
                          transactionStatusTypes[item?.status]?.className
                        }`}
                        style={{ color: item?.status === 2 ? "#FAC710" : "" }}
                      >
                        {transactionStatusTypes[item?.status]?.label}
                      </span>
                    </td>

                    <td className="text-success align-content-end pt-3 ps-0 pe-1">
                      <p
                        className={`font-table ${
                          item?.status === 1
                            ? "text-secondary-blue"
                            : item?.status === 2
                            ? item?.amount < 0
                              ? "text-orange"
                              : "text-secondary-green"
                            : item?.status === 4
                            ? "text-secondary-red"
                            : "text-declined"
                        }`}
                        style={{ fontWeight: "700" }}
                      >
                        {" "}
                        {`${item?.amount < 0 ? "- $" : "$"}${Math.abs(
                          item?.amount
                        ).toLocaleString()}`}
                      </p>
                    </td>
                    <td className="text-dark align-content-end pt-3 font-table">
                      <p> {localizeDate(item?.created)}</p>
                    </td>
                  </tr>
                  {index !== transactions?.length - 1 && (
                    <tr>
                      <td colSpan="100%" style={{ border: "transparent" }}>
                        <div
                          style={{
                            width: "calc(100% - 2px)",
                            height: "2px",
                            backgroundColor:
                              selectedTheme === "dark"
                                ? "rgba(45, 50, 54, 1)"
                                : "rgb(157 157 157)",
                            margin: "0 auto",
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan="100%">
                  <div
                    className="text-center p-4"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                    }}
                  >
                    <img
                      className="mb-3"
                      src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                      alt=""
                      style={{ width: "10rem" }}
                      data-hs-theme-appearance="dark"
                    />
                    <img
                      className="mb-3"
                      src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                      alt=""
                      style={{ width: "10rem" }}
                      data-hs-theme-appearance="default"
                    />
                    <p className="mb-0">
                      {!transactions ? "Loading data" : "No data to show"}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Transaction;
